import { defineAggregate, t, type AggregateState } from '@celeriant/sdk';
import {
  emptySessionState,
  foldSessionEvent,
  hasRegistration,
  registrationNumberFree,
  type SessionEvent,
  type SessionState,
} from './session';
import {
  emptyProjectState,
  foldProjectEvent,
  type ProjectEvent,
  type ProjectState,
} from './project';

// The two aggregates, per docs/sync.md. Both `mode: 'online'` (Pattern B): optimistic concurrency
// on the aggregate version, writes that resolve only when the server acknowledges them, no outbox,
// no undo stack, no local buffering of anything unsent. That is why this app refuses to work
// offline, and it is deliberate: a coach whose phone dropped off the wifi for twenty minutes must
// not come back and dump twenty minutes of stale votes into a session that has moved on.
//
// No `invert` anywhere. Online aggregates have no undo stack and defineAggregate rejects one.
//
// Every reducer below delegates to the pure fold in session.ts / project.ts. The arithmetic lives
// in exactly one place, because two copies of "arrival takes the court mean, rounded up" is two
// chances to round differently, and a device that rounds differently diverges from the hall.

// --- immer bridge ------------------------------------------------------------------------------
// The SDK's reducers receive an immer draft and mutate it in place; our folds are pure and return
// a new state. `plain*` reads the draft out into ordinary objects so the fold never sees a proxy,
// and `writeBack` assigns the result key by key. Both key lists are total over their state type,
// so nothing can be left behind from the previous fold.
//
// Neither bridge returns a projection result, so every event folds to the SDK's no-op result and
// `subscribe` always delivers an empty results list. That is the right shape here: results feed an
// undo stack (online aggregates have none) and let a store react to what one event did, and both
// gateways project from whole state instead, because a server-ordered fold makes state the only
// thing that is true. A result would be wiring with no consumer.

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

function plainSession(state: SessionState): SessionState {
  return {
    status: state.status,
    players: state.players.map((p) => ({
      number: p.number,
      court: p.court,
      score: p.score,
      struckOff: p.struckOff,
      big: p.big,
      lightbulb: p.lightbulb,
      arrivalSeq: p.arrivalSeq,
      upvotes: p.upvotes,
    })),
    correlations: { ...state.correlations },
    numbering: state.numbering,
    numberingEpoch: state.numberingEpoch,
    sigma: state.sigma,
    tolerance: state.tolerance,
    moveLists: state.moveLists.map((l) => ({
      tick: l.tick,
      moves: l.moves.map((m) => ({ player: m.player, from: m.from, to: m.to })),
    })),
    startedAtMs: state.startedAtMs,
    arrivals: state.arrivals,
  };
}

function reduceSession(draft: SessionState, event: SessionEvent): void {
  const next = foldSessionEvent(plainSession(draft), event);
  const target = draft as Mutable<SessionState>;
  target.status = next.status;
  target.players = next.players;
  target.correlations = next.correlations;
  target.numbering = next.numbering;
  target.numberingEpoch = next.numberingEpoch;
  target.sigma = next.sigma;
  target.tolerance = next.tolerance;
  target.moveLists = next.moveLists;
  target.startedAtMs = next.startedAtMs;
  target.arrivals = next.arrivals;
}

function plainProject(state: ProjectState): ProjectState {
  return {
    sessions: state.sessions.map((s) => ({
      sessionId: s.sessionId,
      name: s.name,
      startsAtMs: s.startsAtMs,
    })),
    shares: state.shares.map((s) => ({
      codeHash: s.codeHash,
      sessionId: s.sessionId,
      role: s.role,
      court: s.court,
      disabled: s.disabled,
    })),
  };
}

function reduceProject(draft: ProjectState, event: ProjectEvent): void {
  const next = foldProjectEvent(plainProject(draft), event);
  const target = draft as Mutable<ProjectState>;
  target.sessions = next.sessions;
  target.shares = next.shares;
}

// --- schemas -----------------------------------------------------------------------------------

const court = t.enumOf('white', 'red', 'purple', 'gold');
const role = t.enumOf('frontdesk', 'coach', 'mover', 'admin');
const numbering = t.enumOf('preassigned', 'sequential');
const empty = t.object({});

// --- project -----------------------------------------------------------------------------------

export const project = defineAggregate({
  name: 'grading-day-project-v1',
  mode: 'online',
  encrypt: false,
  state: emptyProjectState(),
  events: {
    // Admin, OCC. Also the event that CREATES the project aggregate and therefore makes the admin
    // device its owner, which is what lets it mint share codes at all. An unguarded creator races
    // a peer's create with nothing to arbitrate, so this one keeps its guard whatever else does.
    sessionScheduled: {
      typeMajor: 1001,
      payload: t.object({ sessionId: t.string(), name: t.string(), startsAtMs: t.number() }),
      apply: (s, p) => reduceProject(s, { kind: 'sessionScheduled', payload: p }),
      precondition: (s, p) => s.sessions.every((x) => x.sessionId !== p.sessionId),
    },
    // Admin, OCC. Code hash, role, court. Never the bearer key itself.
    shareLinkMinted: {
      typeMajor: 1002,
      payload: t.object({
        codeHash: t.string(),
        sessionId: t.string(),
        role,
        court: t.optional(court),
      }),
      apply: (s, p) => reduceProject(s, { kind: 'shareLinkMinted', payload: p }),
      precondition: (s, p) => s.shares.every((x) => x.codeHash !== p.codeHash),
    },
    shareLinkDisabled: {
      typeMajor: 1003,
      payload: t.object({ codeHash: t.string() }),
      apply: (s, p) => reduceProject(s, { kind: 'shareLinkDisabled', payload: p }),
      precondition: (s, p) => s.shares.some((x) => x.codeHash === p.codeHash && !x.disabled),
    },
  },
});

// --- session -----------------------------------------------------------------------------------

export const session = defineAggregate({
  name: 'grading-day-session-v1',
  mode: 'online',
  encrypt: false,
  // Bumped in session-v2, when `SessionState` gained `numbering`. The SDK persists the FOLDED state
  // per aggregate and reuses it on open unless this number has moved (`onlineEngine.ts`), so a
  // device that had this session open before the deploy would otherwise hydrate a state document
  // with no `numbering` key at all and hold `undefined` forever, on exactly the devices already in
  // the hall. A mismatch re-folds from the initial state over the local log, which is where the
  // `preassigned` default comes from, and an untrimmed log makes that free. Online aggregates hold
  // no unsent local events, so the drop-and-re-read fallback loses nothing either.
  schemaVersion: 2,
  state: emptySessionState(),
  events: {
    // Front desk, OCC. The payload never carries a number the DEVICE worked out: the SDK's retry
    // re-sends the identical payload, so a number computed against state read a moment ago would be
    // re-sent after that state moved. On a sequential day it carries no number at all and the
    // reducer assigns one from fold order; on a pre-allocated one it carries the number off the
    // club's spreadsheet, which no amount of aggregate movement can invalidate. Either way the
    // device reads its own number back by correlation id (docs/sync.md). OCC is also what makes the
    // write response safe to fold from, which is why the desk sees its own player the moment
    // execute() resolves.
    playerRegistered: {
      typeMajor: 2001,
      // The FIRST wire minor declared in this repo, and the reason is that this payload GREW a
      // field. The SDK's fold-boundary gate refuses an event whose minor exceeds the one the
      // reader declares and accepts an equal one, and `t.object.parse` copies only the fields the
      // READER declares. Left at the default of 1, a build that predates `number` would accept
      // this event, drop the number without a word, and fold player 42 as player 1: two devices in
      // one hall disagreeing about which human is which, silently, for a whole session. At 2 that
      // build refuses the event and latches update-required instead, which is the loud failure
      // INVARIANTS rule 17 promises. Bump this whenever a payload gains a field.
      typeMinor: 2,
      // `number` is OPTIONAL and is what a PRE-ALLOCATED day carries: the club numbered its players
      // off the website sign-ups and the desk types the one on the printout (session-v2/goal.md).
      // Absent, the reducer assigns the smallest free number, which is the sequentially numbered
      // day and the only shape that existed before.
      payload: t.object({ court, correlationId: t.string(), number: t.optional(t.number()) }),
      apply: (s, p) => reduceSession(s, { kind: 'playerRegistered', payload: p }),
      // A precondition it did NOT have before, and the reason it can have one now: the payload's
      // number is read off paper, not computed against the state this guard is checked on, so a
      // retry re-sends something that still means what it meant. Two clauses, and the order is the
      // point. A correlation id already in state is THIS desk's own write coming back after a lost
      // ack, and it must pass or the ladder refuses the retry it exists to survive (docs/sync.md).
      // Otherwise the number has to be free, which is what turns "two people were handed 42" into a
      // refusal on the second desk's screen instead of a second player nobody can tell apart.
      precondition: (s, p) =>
        hasRegistration(s, p.correlationId) || registrationNumberFree(s, p.number),
    },
    // Admin, OCC. Which of the two numbering modes the desks in this hall are working in. An event
    // and not a device setting, for the same reason the two tick knobs are: four desks have to
    // agree, and a setting that lived on one laptop would leave the other three asking humans for
    // numbers the club never allocated.
    numberingModeSet: {
      typeMajor: 2012,
      payload: t.object({ mode: numbering }),
      apply: (s, p) => reduceSession(s, { kind: 'numberingModeSet', payload: p }),
      precondition: (_s, p) => p.mode === 'preassigned' || p.mode === 'sequential',
    },
    // Admin, OCC. The precondition guards the state machine: scheduled -> running -> stopped ->
    // running, and starting an already running session is not a thing that happened.
    // The payload gained an OPTIONAL `startedAtMs` in P4: the admin device stamps its own clock so
    // ticks run from the ACTUAL start (docs/session-lifecycle.md). Optional and not required,
    // because a device on the P1 build writes `{}` and its event must still fold. The FOLD keeps
    // the first value it is ever given, which is what makes a restart harmless.
    sessionStarted: {
      typeMajor: 2002,
      payload: t.object({ startedAtMs: t.optional(t.number()) }),
      apply: (s, p) => reduceSession(s, { kind: 'sessionStarted', payload: p }),
      precondition: (s) => s.status !== 'running',
    },
    sessionStopped: {
      typeMajor: 2003,
      payload: empty,
      apply: (s) => reduceSession(s, { kind: 'sessionStopped', payload: {} }),
      precondition: (s) => s.status === 'running',
    },
    // Admin, OCC. The outlier constant, tunable on the day.
    thresholdSet: {
      typeMajor: 2004,
      payload: t.object({ sigma: t.number() }),
      apply: (s, p) => reduceSession(s, { kind: 'thresholdSet', payload: p }),
      precondition: (_s, p) => Number.isFinite(p.sigma) && p.sigma > 0,
    },
    // Admin, OCC. The rebalance knob. Same shape and same reason as `thresholdSet`: a local setting
    // would make the tick unreproducible from the log, and two admin devices could then disagree
    // about how balanced the hall is meant to be.
    rebalanceToleranceSet: {
      typeMajor: 2011,
      payload: t.object({ tolerance: t.number() }),
      apply: (s, p) => reduceSession(s, { kind: 'rebalanceToleranceSet', payload: p }),
      // At least one whole player. Zero would demand exactly level courts, which a headcount that
      // is not divisible by four can never be (docs/rotation.md, "Balance").
      precondition: (_s, p) => Number.isInteger(p.tolerance) && p.tolerance >= 1,
    },
    // Admin device, OCC. Exactly one list must land per tick, or two movers walk contradictory
    // instructions around the hall (docs/rotation.md).
    moveListComputed: {
      typeMajor: 2005,
      payload: t.object({
        tick: t.number(),
        moves: t.array(t.object({ player: t.number(), from: court, to: court })),
      }),
      apply: (s, p) => reduceSession(s, { kind: 'moveListComputed', payload: p }),
      precondition: (s, p) => s.moveLists.every((l) => l.tick !== p.tick),
    },

    // Below here: occ:false. A pure append whose reducer commutes with its peers and whose
    // validity does not depend on the state it lands on. Dropping the version guard means the
    // write contends for nothing, so twelve devices firing at once all land instead of trading
    // 409s. The cost is that execute() resolves BEFORE the event reaches state: it arrives on the
    // next catch-up or realtime poke. Nothing below needs an answer beyond "it landed".
    //
    // None of them may carry a precondition — defineAggregate rejects the pairing, because a check
    // made without a version guard proves nothing: the state can move between the check and the
    // commit. The folds are total instead, and ignore what does not apply.

    // Coach and mover. High volume, the reason the flag exists.
    voteCast: {
      typeMajor: 2006,
      payload: t.object({ player: t.number(), direction: t.enumOf('up', 'down') }),
      apply: (s, p) => reduceSession(s, { kind: 'voteCast', payload: p }),
      occ: false,
    },
    // Coach. Sets a value, never toggles: two coaches tapping the same flag in the same moment
    // would otherwise cancel into a no-op, which is not what either of them meant.
    attributeSet: {
      typeMajor: 2007,
      payload: t.object({
        player: t.number(),
        attribute: t.enumOf('big', 'lightbulb'),
        value: t.boolean(),
      }),
      apply: (s, p) => reduceSession(s, { kind: 'attributeSet', payload: p }),
      occ: false,
    },
    // Mover. The tick-off, and the event that actually moves the player.
    playerMoved: {
      typeMajor: 2008,
      payload: t.object({
        player: t.number(),
        toCourt: court,
        placement: t.enumOf('mean', 'bottom', 'top'),
      }),
      apply: (s, p) => reduceSession(s, { kind: 'playerMoved', payload: p }),
      occ: false,
    },
    // Mover. Removes a departed player from the coach screens and from the report.
    playerStruckOff: {
      typeMajor: 2009,
      payload: t.object({ player: t.number() }),
      apply: (s, p) => reduceSession(s, { kind: 'playerStruckOff', payload: p }),
      occ: false,
    },
    playerReinstated: {
      typeMajor: 2010,
      payload: t.object({ player: t.number(), court }),
      apply: (s, p) => reduceSession(s, { kind: 'playerReinstated', payload: p }),
      occ: false,
    },
  },
});

export type ProjectAggregateState = AggregateState<typeof project>;
export type SessionAggregateState = AggregateState<typeof session>;

// P1 has no project screen and no session picker, so the app opens one well-known session. Role
// routing and the run list land in P2; until then this is the id the shell boots against.
export const DEV_SESSION_ID = 'dev-session';
export const DEV_PROJECT_ID = 'dev-project';
